import {
  ERecurrenceEndCondition,
  ERecurrenceFrequency,
  IRecurrenceRequest,
} from 'services/TaskManagementApi/types/TaskManagementResponse';
import { formatDateDisplayWithoutOffset } from 'utils';

// OWA-11923 - the "Recurring Setup" section of the New Recurring Task form.

export const RECURRING_TASKS_PATH = '/recurring-tasks';

export const getRecurringTaskEditPath = (taskId: string): string => `${RECURRING_TASKS_PATH}/${taskId}/edit`;

export interface IRecurrenceFormValues {
  /** YYYY-MM-DD in the user's local calendar */
  startDate: string;
  endCondition: ERecurrenceEndCondition;
  /** YYYY-MM-DD, only with "On" */
  endDate: string;
  // The number fields are the raw `ui/NumberInput` strings, parsed when the request is built.
  endAfterOccurrences: string;
  interval: string;
  /** '' until the user picks one, there is no default */
  frequency: ERecurrenceFrequency | '';
  /** ISO days of week, 1 = Monday ... 7 = Sunday */
  daysOfWeek: number[];
  /** id of one of `getMonthlyOptions(startDate)`, '' for none */
  monthlyOption: string;
  deadlineOffsetDays: string;
  leadTimeDays: string;
}

export const frequencyOptions = [
  { value: ERecurrenceFrequency.DAY, label: 'days' },
  { value: ERecurrenceFrequency.WEEK, label: 'weeks' },
  { value: ERecurrenceFrequency.MONTH, label: 'months' },
  { value: ERecurrenceFrequency.YEAR, label: 'years' },
];

export const endConditionOptions = [
  { value: ERecurrenceEndCondition.NEVER, label: 'Never' },
  { value: ERecurrenceEndCondition.UNTIL_DATE, label: 'On ...' },
  { value: ERecurrenceEndCondition.AFTER_N, label: 'After ...' },
];

// Index = ISO day of week - 1.
const WEEKDAYS = ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday', 'Sunday'];

export const weekdayOptions = WEEKDAYS.map((name, index) => ({ value: index + 1, label: name.slice(0, 3) }));

// TMS reads week 5 as the last such weekday, also in months that only have four of them.
const WEEK_OF_MONTH_ORDINALS = ['first', 'second', 'third', 'fourth', 'last'];

/** A plain calendar date out of a picker's ISO string, without going through a timezone. */
export const toCalendarDate = (dateString: string): string => dateString.slice(0, 10);

/** Today in the user's local calendar, YYYY-MM-DD. */
export const getLocalToday = (now: Date = new Date()): string => {
  const pad = (n: number) => String(n).padStart(2, '0');
  return `${now.getFullYear()}-${pad(now.getMonth() + 1)}-${pad(now.getDate())}`;
};

const parseCalendarDate = (date: string): { day: number; isoWeekday: number } | null => {
  const match = /^(\d{4})-(\d{2})-(\d{2})$/.exec(date ?? '');
  if (!match) {
    return null;
  }
  const [year, month, day] = [Number(match[1]), Number(match[2]), Number(match[3])];
  // Built from its parts, so it is that day at local midnight, whatever the timezone.
  const jsWeekday = new Date(year, month - 1, day).getDay();
  return { day, isoWeekday: jsWeekday === 0 ? 7 : jsWeekday };
};

export interface IMonthlyOption {
  id: string;
  label: string;
  dayOfMonth: number | null;
  weekOfMonth: number | null;
  dayOfWeek: number | null;
}

/** "On day D" and "On the <ordinal> <weekday>", both read off the start date. */
export const getMonthlyOptions = (startDate: string): IMonthlyOption[] => {
  const parsed = parseCalendarDate(startDate);
  if (!parsed) {
    return [];
  }
  const { day, isoWeekday } = parsed;
  const weekOfMonth = Math.ceil(day / 7);

  return [
    {
      id: `day-${day}`,
      label: `On day ${day}`,
      dayOfMonth: day,
      weekOfMonth: null,
      dayOfWeek: null,
    },
    {
      id: `weekday-${weekOfMonth}-${isoWeekday}`,
      label: `On the ${WEEK_OF_MONTH_ORDINALS[weekOfMonth - 1]} ${WEEKDAYS[isoWeekday - 1]}`,
      dayOfMonth: null,
      weekOfMonth,
      dayOfWeek: isoWeekday,
    },
  ];
};

const toInteger = (value: string): number => parseInt(value, 10);

/**
 * The `recurrence` body of POST v1/recurring-tasks. TMS ignores what doesn't belong to the chosen
 * frequency and end condition, but we still send those keys as clean nulls.
 */
export const recurrenceFormToRequest = (values: IRecurrenceFormValues): IRecurrenceRequest => {
  const isWeekly = values.frequency === ERecurrenceFrequency.WEEK;
  const isMonthly = values.frequency === ERecurrenceFrequency.MONTH;
  const monthlyOption = isMonthly
    ? getMonthlyOptions(values.startDate).find(option => option.id === values.monthlyOption)
    : undefined;

  return {
    startDate: values.startDate,
    frequency: values.frequency as ERecurrenceFrequency,
    interval: toInteger(values.interval),
    daysOfWeek: isWeekly ? [...values.daysOfWeek].sort((a, b) => a - b) : null,
    dayOfMonth: monthlyOption?.dayOfMonth ?? null,
    weekOfMonth: monthlyOption?.weekOfMonth ?? null,
    dayOfWeek: monthlyOption?.dayOfWeek ?? null,
    endCondition: values.endCondition,
    endAfterOccurrences:
      values.endCondition === ERecurrenceEndCondition.AFTER_N ? toInteger(values.endAfterOccurrences) : null,
    endDate: values.endCondition === ERecurrenceEndCondition.UNTIL_DATE ? values.endDate : null,
    deadlineOffsetDays: toInteger(values.deadlineOffsetDays),
    leadTimeDays: toInteger(values.leadTimeDays),
  };
};

/**
 * OWA-11997 - the reverse of `recurrenceFormToRequest`, to fill the Edit form from TMS. A monthly
 * rule goes back to the option id `getMonthlyOptions(startDate)` gives it.
 */
export const recurrenceToFormValues = (recurrence: IRecurrenceRequest): IRecurrenceFormValues => {
  let monthlyOption = '';
  if (recurrence.frequency === ERecurrenceFrequency.MONTH) {
    if (recurrence.dayOfMonth) {
      monthlyOption = `day-${recurrence.dayOfMonth}`;
    } else if (recurrence.weekOfMonth && recurrence.dayOfWeek) {
      monthlyOption = `weekday-${recurrence.weekOfMonth}-${recurrence.dayOfWeek}`;
    }
  }

  return {
    startDate: recurrence.startDate,
    endCondition: recurrence.endCondition,
    endDate: recurrence.endDate ?? '',
    endAfterOccurrences: recurrence.endAfterOccurrences === null ? '' : String(recurrence.endAfterOccurrences),
    interval: String(recurrence.interval),
    frequency: recurrence.frequency,
    daysOfWeek: recurrence.frequency === ERecurrenceFrequency.WEEK ? [...(recurrence.daysOfWeek ?? [])] : [],
    monthlyOption,
    deadlineOffsetDays: String(recurrence.deadlineOffsetDays),
    leadTimeDays: String(recurrence.leadTimeDays),
  };
};

export const makeInitialRecurrence =(today: string = getLocalToday()): IRecurrenceFormValues => ({
  startDate: today,
  endCondition: ERecurrenceEndCondition.NEVER,
  endDate: '',
  endAfterOccurrences: '',
  interval: '1',
  frequency: '',
  daysOfWeek: [],
  monthlyOption: '',
  deadlineOffsetDays: '0',
  leadTimeDays: '0',
});

const FREQUENCY_UNITS: Record<ERecurrenceFrequency, [string, string]> = {
  [ERecurrenceFrequency.DAY]: ['Day', 'Days'],
  [ERecurrenceFrequency.WEEK]: ['Week', 'Weeks'],
  [ERecurrenceFrequency.MONTH]: ['Month', 'Months'],
  [ERecurrenceFrequency.YEAR]: ['Year', 'Years'],
};

/** 1 -> "1st", 2 -> "2nd", 11 -> "11th", 23 -> "23rd" */
export const toOrdinal = (n: number): string => {
  const lastTwo = n % 100;
  if (lastTwo >= 11 && lastTwo <= 13) {
    return `${n}th`;
  }
  const suffix = { 1: 'st', 2: 'nd', 3: 'rd' }[n % 10] ?? 'th';
  return `${n}${suffix}`;
};

export interface IRecurrenceRule {
  frequency: ERecurrenceFrequency;
  interval: number;
  daysOfWeek?: number[] | null;
  dayOfMonth?: number | null;
  weekOfMonth?: number | null;
  dayOfWeek?: number | null;
}

/**
 * OWA-11996 - the Frequency column of the recurring tasks list, e.g. "Every 2 Weeks on Mon, Wed",
 * "Every Month on the 25th", "Every 3 Months on the last Friday". Singular when the interval is 1.
 */
export const formatRecurrenceFrequency = (rule: IRecurrenceRule): string => {
  const [singular, plural] = FREQUENCY_UNITS[rule.frequency] ?? ['', ''];
  const every = rule.interval === 1 ? `Every ${singular}` : `Every ${rule.interval} ${plural}`;

  if (rule.frequency === ERecurrenceFrequency.WEEK && rule.daysOfWeek?.length) {
    const days = [...rule.daysOfWeek]
      .sort((a, b) => a - b)
      .map(day => WEEKDAYS[day - 1]?.slice(0, 3))
      .filter(Boolean);
    return days.length ? `${every} on ${days.join(', ')}` : every;
  }

  if (rule.frequency === ERecurrenceFrequency.MONTH) {
    if (rule.dayOfMonth) {
      return `${every} on the ${toOrdinal(rule.dayOfMonth)}`;
    }
    const ordinal = rule.weekOfMonth ? WEEK_OF_MONTH_ORDINALS[rule.weekOfMonth - 1] : undefined;
    const weekday = rule.dayOfWeek ? WEEKDAYS[rule.dayOfWeek - 1] : undefined;
    if (ordinal && weekday) {
      return `${every} on the ${ordinal} ${weekday}`;
    }
  }

  return every;
};

/**
 * A YYYY-MM-DD calendar date in the app's display format. Built from its parts, so the day can't
 * shift with the user's timezone. Anything else (including null) gives ''.
 */
export const formatCalendarDateDisplay = (date: string | null): string => {
  const match = /^(\d{4})-(\d{2})-(\d{2})$/.exec(date ?? '');
  if (!match) {
    return '';
  }
  return formatDateDisplayWithoutOffset(new Date(Number(match[1]), Number(match[2]) - 1, Number(match[3])));
};
